import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import styled from "styled-components";
import GlobalStyles from "./styles/GlobalStyles";
import Header from "./components/Header";
import Footer from "./components/Footer";
import MainLanding from "./components/MainLanding";

import AboutUs from "./components/Aboutus";
import Features from "./components/Features";
import ExploreMore from "./components/ExploreMore";
import Discover from "./components/Discover";
import UIUXDesign from "./components/UIUXDesign";
import WebDevelopment from "./components/WebDevelopment";
import DatabaseManagement from "./components/DatabaseManagement";
import Marketing from "./components/marketing";
import Career from "./components/Career";
import Contact from './components/Contact';
import CareerPage from './components/CareerPage';
import CareerPaths from './components/CareerPaths';
import Internships from './components/Internships';
import JobReadyProgram from "./components/JobReadyProgram";
import TwoYearProgram from "./components/TwoYearProgram";
import ThreeYearProgram from "./components/ThreeYearProgram";
import AIReadyJobProgram from "./components/AIReadyJobProgram";
import Products from "./components/Products";
// PaymentCallback remains exported from the payment module for product integrations.
// eslint-disable-next-line no-unused-vars
import { Payment, PaymentCallback, PaymentDemo } from "./components/Payment";
import PaymentGatewayRouter from "./components/Payment/PaymentGatewayRouter";
import RestoPayment from "./components/Payment/RestoPayment";
import RestoPaymentCallback from "./components/Payment/RestoPaymentCallback";
import RestoPaymentTest from "./components/Payment/RestoPaymentTest";
import RazorpayPayment from "./components/Payment/RazorpayPayment";
import TrainingEnroll from "./components/TrainingEnroll";
import InternshipEnroll from "./components/InternshipEnroll";

// Policy pages
import PrivacyPolicy from "./components/policies/PrivacyPolicy";
import TermsAndConditions from "./components/policies/TermsAndConditions";
import RefundPolicy from "./components/policies/RefundPolicy";
import CompanyInfo from "./components/policies/CompanyInfo";

// Wrapper styling
const ContentWrapper = styled.div`
  padding-top: ${props => props.isHome ? '0px' : '58px'}; /* No padding on home for Hero overlap */
  background-color: var(--gray-0);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: 1024px) {
    padding-top: ${props => props.isHome ? '0px' : '56px'};
  }

  @media (max-width: 768px) {
    padding-top: ${props => props.isHome ? '0px' : '54px'};
  }
  
  @media (max-width: 480px) {
    padding-top: ${props => props.isHome ? '0px' : '52px'};
  }
`;

// Layout wrapper to handle location-based styles
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isStandalone = location.pathname.startsWith('/payment');
  
  return (
    <ContentWrapper isHome={isHome || isStandalone}>
      {children}
    </ContentWrapper>
  );
};

// Home page component with section IDs for SPA scroll navigation
const Home = () => {
  const location = useLocation();

  // Handle hash-based scroll when navigating from other pages (e.g. /#about)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const scrollToSection = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      };
      // Small delay to let the page render first
      const timer = setTimeout(scrollToSection, 150);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  return (
    <MainLanding />
  );
};

// Component to conditionally render Footer
const FooterWrapper = () => {
  const location = useLocation();

  // Add any route where footer shouldn't appear
  const noFooterRoutes = [
    '/career-paths',
    '/payment',
    '/payment/callback',
    '/payment-demo',
    '/payment/resto',
    '/payment/resto/test',
    '/payment/resto/callback',
    '/payment/razorpay',
    '/enroll/training',
    '/enroll/internship'
  ];
  const showFooter = !noFooterRoutes.includes(location.pathname);

  return showFooter ? <Footer /> : null;
};

// Component to conditionally render Header
const HeaderWrapper = () => {
  const location = useLocation();

  // Hide header on payment pages for clean payment experience
  const noHeaderRoutes = [
    '/payment',
    '/payment/callback',
    '/payment-demo',
    '/payment/resto',
    '/payment/resto/test',
    '/payment/resto/callback',
    '/payment/razorpay',
    '/enroll/training',
    '/enroll/internship'
  ];
  const showHeader = !noHeaderRoutes.includes(location.pathname);

  return showHeader ? <Header /> : null;
};

// Main App component
const App = () => {
  return (
    <Router>
      <GlobalStyles />
      <HeaderWrapper />
      <LayoutWrapper>
        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/service" element={<Features />} />
          <Route path="/explore-more" element={<ExploreMore />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/ui-ux-design" element={<UIUXDesign />} />
          <Route path="/web-development" element={<WebDevelopment />} />
          <Route path="/database-management" element={<DatabaseManagement />} />
          <Route path="/marketing" element={<Marketing />} />
          <Route path="/products" element={<Products />} />

          {/* Career Pages */}
          <Route path="/career" element={<Career />} />
          <Route path="/careerpage" element={<CareerPage />} />
          <Route path="/career-paths" element={<CareerPaths />} />
          <Route path="/internships" element={<Internships />} />
          <Route path="/job-ready-program" element={<JobReadyProgram />} />
          <Route path="/ai-ready-program" element={<AIReadyJobProgram />} />
          <Route path="/two-year-program" element={<TwoYearProgram />} />
          <Route path="/three-year-program" element={<ThreeYearProgram />} />

          {/* Payment Routes */}
          <Route path="/payment" element={<Payment />} />
          {/* Main callback route - handles both regular and resto payments */}
          <Route path="/payment/callback" element={<RestoPaymentCallback />} />
          <Route path="/payment-demo" element={<PaymentDemo />} />
          {/* Resto Payment Integration (Popup Window) */}
          <Route path="/payment/resto" element={<PaymentGatewayRouter />} />
          <Route path="/payment/phonepe" element={<RestoPayment />} />
          <Route path="/payment/resto/test" element={<RestoPaymentTest />} />
          {/* Razorpay Payment Integration (Popup Window) — shared across apps */}
          <Route path="/payment/razorpay" element={<RazorpayPayment />} />

          {/* Hidden Enrollment Pages - Share via direct link only */}
          <Route path="/enroll/training" element={<TrainingEnroll />} />
          <Route path="/enroll/internship" element={<InternshipEnroll />} />

          {/* Policy and Legal Pages */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/company-info" element={<CompanyInfo />} />
        </Routes>
      </LayoutWrapper>
      <FooterWrapper />
    </Router>
  );
};

export default App;
